import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Boxes,
  ChevronDown,
  ChevronRight,
  Clock,
  KeyRound,
  Server,
  Users,
  Zap,
} from 'lucide-react';
import { Modal } from '../../ui';
import { useTheme } from '../../context/ThemeContext';
import { RequestStatus, type UsageRecord } from '../../gql/types';
import { providerLabel } from '../shared/providerLabels';
import { formatDateTime, formatNumber } from '../../lib/format';

// Detail panel for a single usage record (W-DEBUG-1). Opened by clicking a row in the
// UsageFeed. Goal: surface every field on the record cleanly + parse the failed-request
// error_code (comma-separated `provider:reason` summaries written by `record_failed_usage/3`
// in `responses_controller.ex`) into structured per-attempt rows so an operator can see at
// a glance which providers were tried and why each one broke. Previously this info was
// jammed into a single mono-font cell and got truncated past ~80 chars.

interface ParsedAttempt {
  provider: string;
  reason: string;
}

/**
 * Splits the `error_code` column into structured per-attempt rows.
 *
 * The backend writes the failed-request `error_code` as a comma-separated list of
 * `provider:summary` pairs (see `attempts_error_code/2` in `responses_controller.ex`).
 * A `provider:` prefix is whatever `Provider.type` atom serializes to (lowercase snake_case).
 * Anything after the first `:` is the summarized reason — we don't try to re-parse it, we
 * just render it as a mono chip so the operator can read the exact code.
 *
 * Splitting on commas is safe because reason summaries are produced by
 * `summarize_reason/1`, which emits `http_<status>`, `invalid_body_<status>`,
 * `no_credentials`, `unsupported_<cap>`, or an `inspect/1`-truncated term — none of which
 * contain a literal comma. The defensive fallback below treats a non-parseable string as a
 * single opaque attempt so we never lose the original detail.
 */
function parseAttempts(errorCode: string | null | undefined): ParsedAttempt[] {
  if (!errorCode) return [];
  const trimmed = errorCode.trim();
  if (trimmed === '') return [];

  const parts = trimmed.split(',');
  const attempts: ParsedAttempt[] = [];

  for (const raw of parts) {
    const piece = raw.trim();
    if (piece === '') continue;
    const idx = piece.indexOf(':');
    if (idx === -1) {
      attempts.push({ provider: 'unknown', reason: piece });
    } else {
      attempts.push({
        provider: piece.slice(0, idx).trim(),
        reason: piece.slice(idx + 1).trim(),
      });
    }
  }

  return attempts;
}

interface UsageRecordDetailProps {
  isOpen: boolean;
  onClose: () => void;
  record: UsageRecord | null;
}

export function UsageRecordDetail({ isOpen, onClose, record }: UsageRecordDetailProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Parse on every render (the modal only mounts when open + record is non-null, and the
  // error_code string is short, so this isn't worth memoising on identity alone).
  const attempts = useMemo(
    () => parseAttempts(record?.errorCode ?? null),
    [record?.errorCode],
  );

  if (!record) return null;

  const isFailure = record.status === RequestStatus.Failed;
  const totalTokens =
    record.promptTokens + record.completionTokens + record.reasoningTokens;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Request details"
      subtitle={`${record.canonicalModel} · ${providerLabel(record.chosenProvider)} · ${formatDateTime(record.insertedAt)}`}
      icon={
        isFailure ? (
          <AlertTriangle className="h-7 w-7" aria-hidden="true" />
        ) : (
          <Boxes className="h-7 w-7" aria-hidden="true" />
        )
      }
    >
      <div className="space-y-6">
        {/* ── Error envelope (failure only) ─────────────────────────────── */}
        {isFailure && (
          <ErrorSection
            isDark={isDark}
            attempts={attempts}
            rawErrorCode={record.errorCode}
          />
        )}

        {/* ── Routing ────────────────────────────────────────────────────── */}
        <Section
          isDark={isDark}
          title="Routing"
          icon={<Server className="h-4 w-4" aria-hidden="true" />}
        >
          <FieldGrid isDark={isDark}>
            <Field
              isDark={isDark}
              label="Canonical model"
              value={record.canonicalModel}
              mono
            />
            <Field
              isDark={isDark}
              label="Provider model (requested)"
              value={record.providerModel}
              mono
            />
            {record.actualProviderModel &&
              record.actualProviderModel !== record.providerModel && (
                <Field
                  isDark={isDark}
                  label="Provider model (served)"
                  value={record.actualProviderModel}
                  mono
                  tone="warn"
                />
              )}
            <Field
              isDark={isDark}
              label="Chosen provider"
              value={providerLabel(record.chosenProvider)}
            />
            <Field
              isDark={isDark}
              label="Routed reason"
              value={record.routedReason.toLowerCase()}
              mono
            />
            <Field
              isDark={isDark}
              label="Status"
              value={record.status.toLowerCase()}
              mono
              tone={isFailure ? 'danger' : 'ok'}
            />
            <Field
              isDark={isDark}
              label="Streamed"
              value={record.streamed ? 'true' : 'false'}
              mono
            />
            {record.policyName && (
              <Field
                isDark={isDark}
                label="Routing policy"
                value={record.policyName}
                mono
              />
            )}
          </FieldGrid>
        </Section>

        {/* ── Routing chain (2026-06-06) ──────────────────────────────────────
            Renders the full structured chain: candidates considered, per-hop
            fallback walk, and the winner. Collapsible because the chain is verbose
            and the operator usually only drills in when debugging an unexpected
            route. */}
        {record.routingDecision && (
          <RoutingChainPanel
            isDark={isDark}
            decision={record.routingDecision}
          />
        )}

        {/* ── Timing ─────────────────────────────────────────────────────── */}
        <Section
          isDark={isDark}
          title="Timing"
          icon={<Clock className="h-4 w-4" aria-hidden="true" />}
        >
          <FieldGrid isDark={isDark}>
            <Field isDark={isDark} label="Queue" value={`${formatNumber(record.queueMs)} ms`} />
            <Field
              isDark={isDark}
              label="Upstream"
              value={`${formatNumber(record.upstreamMs)} ms`}
            />
            <Field isDark={isDark} label="Total" value={`${formatNumber(record.totalMs)} ms`} />
          </FieldGrid>
        </Section>

        {/* ── Tokens ─────────────────────────────────────────────────────── */}
        <Section
          isDark={isDark}
          title="Tokens"
          icon={<Zap className="h-4 w-4" aria-hidden="true" />}
        >
          <FieldGrid isDark={isDark}>
            <Field
              isDark={isDark}
              label="Input"
              value={formatNumber(record.promptTokens)}
            />
            <Field
              isDark={isDark}
              label="Output"
              value={formatNumber(record.completionTokens)}
            />
            <Field
              isDark={isDark}
              label="Reasoning"
              value={formatNumber(record.reasoningTokens)}
            />
            <Field isDark={isDark} label="Total" value={formatNumber(totalTokens)} />
            <Field
              isDark={isDark}
              label="Cost estimate"
              value={
                record.costEstimate != null
                  ? `$${record.costEstimate.toFixed(6)}`
                  : '—'
              }
            />
          </FieldGrid>
        </Section>

        {/* ── Tenancy ────────────────────────────────────────────────────── */}
        <Section
          isDark={isDark}
          title="Tenancy"
          icon={<Users className="h-4 w-4" aria-hidden="true" />}
        >
          <FieldGrid isDark={isDark}>
            <Field isDark={isDark} label="Client" value={record.client.name} />
            <Field isDark={isDark} label="Application" value={record.application.name} />
          </FieldGrid>
        </Section>

        {/* ── API key ────────────────────────────────────────────────────── */}
        <Section
          isDark={isDark}
          title="API key"
          icon={<KeyRound className="h-4 w-4" aria-hidden="true" />}
        >
          <FieldGrid isDark={isDark}>
            <Field
              isDark={isDark}
              label="Prefix"
              value={record.apiKey.keyPrefix}
              mono
            />
            <Field
              isDark={isDark}
              label="Record ID"
              value={record.id}
              mono
            />
          </FieldGrid>
        </Section>

        {/* ── Per-hop telemetry (2026-06-06): each hop in its own collapsible panel so an
            operator can step through "what did the consumer send", "what did we forward",
            "what came back from the upstream", and "what did we reply" without scrolling
            through one giant blob. Empty panels collapse to a one-line "not captured"
            note rather than vanishing — so the absence is itself debuggable. */}
        <TelemetryPanel
          isDark={isDark}
          title="Proxy request"
          subtitle="What the consumer sent us"
          payload={record.proxyRequest}
          testId="usage-detail-proxy-request"
        />
        <TelemetryPanel
          isDark={isDark}
          title="Upstream request"
          subtitle="What we forwarded to the provider (auth headers redacted)"
          payload={record.upstreamRequest}
          testId="usage-detail-upstream-request"
        />
        <TelemetryPanel
          isDark={isDark}
          title="Upstream response"
          subtitle="What the provider sent back"
          payload={record.upstreamResponse}
          testId="usage-detail-upstream-response"
        />
        <TelemetryPanel
          isDark={isDark}
          title="Proxy response"
          subtitle="What we replied to the consumer"
          payload={record.proxyResponse}
          testId="usage-detail-proxy-response"
        />
      </div>
    </Modal>
  );
}

// Structured shape of the routing_decision JSON column. The fields mirror what
// `responses_controller.build_routing_decision/1` writes on the backend — kept loose
// enough that adding a new field server-side doesn't break the SPA.
interface RoutingDecisionShape {
  policy?: { id: string; name: string } | null;
  candidates?: Array<{
    position: number;
    provider_type: string;
    provider_id?: string | null;
    provider_model?: string | null;
    saturation_skips?: number;
    health_skips?: number;
  }>;
  attempts?: Array<{
    provider_type: string;
    provider_model?: string | null;
    reason?: string | null;
  }>;
  winner?: {
    provider_type: string;
    provider_model?: string | null;
    routed_reason?: string | null;
  };
}

function RoutingChainPanel({
  isDark,
  decision,
}: {
  isDark: boolean;
  decision: RoutingDecisionShape;
}) {
  const [open, setOpen] = useState(false);
  const policyLabel = decision.policy?.name ?? '(no policy)';
  const winnerLabel = decision.winner
    ? `${decision.winner.provider_type} → ${decision.winner.provider_model ?? '?'} (${decision.winner.routed_reason ?? '?'})`
    : '(no winner)';
  const attemptCount = decision.attempts?.length ?? 0;
  const candidateCount = decision.candidates?.length ?? 0;

  return (
    <div
      data-testid="usage-detail-routing-chain"
      className={`rounded-xl border ${
        isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-slate-50/60'
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors ${
          isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-slate-100/60'
        }`}
      >
        <span className="flex items-center gap-2">
          {open ? (
            <ChevronDown
              className={`h-4 w-4 ${isDark ? 'text-white/50' : 'text-slate-500'}`}
              aria-hidden="true"
            />
          ) : (
            <ChevronRight
              className={`h-4 w-4 ${isDark ? 'text-white/50' : 'text-slate-500'}`}
              aria-hidden="true"
            />
          )}
          <span className={`text-sm font-medium ${isDark ? 'text-white/85' : 'text-slate-800'}`}>
            Routing chain
          </span>
          <span className={`text-[11px] ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
            policy: {policyLabel} · {attemptCount} fallback{attemptCount === 1 ? '' : 's'} ·
            {candidateCount} candidate{candidateCount === 1 ? '' : 's'}
          </span>
        </span>
      </button>
      {open && (
        <div
          className={`border-t px-4 py-3 ${
            isDark ? 'border-white/[0.06]' : 'border-slate-200'
          }`}
        >
          <div className="space-y-3">
            <div>
              <p
                className={`text-[10px] font-semibold uppercase tracking-widest ${
                  isDark ? 'text-white/40' : 'text-slate-500'
                }`}
              >
                Winner
              </p>
              <p
                className={`mt-0.5 font-mono text-xs ${
                  isDark ? 'text-emerald-300' : 'text-emerald-700'
                }`}
              >
                {winnerLabel}
              </p>
            </div>

            {attemptCount > 0 && (
              <div>
                <p
                  className={`text-[10px] font-semibold uppercase tracking-widest ${
                    isDark ? 'text-white/40' : 'text-slate-500'
                  }`}
                >
                  Fallback walk · {attemptCount} attempt{attemptCount === 1 ? '' : 's'} before winner
                </p>
                <ol className="mt-1 space-y-1">
                  {decision.attempts?.map((a, idx) => (
                    <li
                      key={idx}
                      data-testid="routing-chain-attempt"
                      className={`flex items-baseline gap-2 rounded-md px-2 py-1 font-mono text-[11px] ${
                        isDark ? 'bg-rose-500/[0.06]' : 'bg-rose-50'
                      }`}
                    >
                      <span
                        className={`flex-shrink-0 ${
                          isDark ? 'text-white/40' : 'text-slate-400'
                        }`}
                      >
                        {idx + 1}.
                      </span>
                      <span
                        className={`flex-shrink-0 ${
                          isDark ? 'text-rose-300' : 'text-rose-700'
                        }`}
                      >
                        {a.provider_type}
                      </span>
                      {a.provider_model && (
                        <span className={isDark ? 'text-white/55' : 'text-slate-500'}>
                          {a.provider_model}
                        </span>
                      )}
                      {a.reason && (
                        <span className={isDark ? 'text-white/65' : 'text-slate-700'}>
                          · {a.reason}
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {candidateCount > 0 && (
              <div>
                <p
                  className={`text-[10px] font-semibold uppercase tracking-widest ${
                    isDark ? 'text-white/40' : 'text-slate-500'
                  }`}
                >
                  Candidates at decision time
                </p>
                <ol className="mt-1 space-y-1">
                  {decision.candidates?.map((c) => (
                    <li
                      key={c.position}
                      data-testid="routing-chain-candidate"
                      className={`flex items-baseline gap-2 rounded-md px-2 py-1 font-mono text-[11px] ${
                        isDark ? 'bg-white/[0.03]' : 'bg-slate-100'
                      }`}
                    >
                      <span
                        className={`flex-shrink-0 ${
                          isDark ? 'text-white/40' : 'text-slate-400'
                        }`}
                      >
                        {c.position + 1}.
                      </span>
                      <span className={isDark ? 'text-white/80' : 'text-slate-700'}>
                        {c.provider_type}
                      </span>
                      {c.provider_model && (
                        <span className={isDark ? 'text-white/55' : 'text-slate-500'}>
                          {c.provider_model}
                        </span>
                      )}
                      {(c.saturation_skips ?? 0) > 0 && (
                        <span className={isDark ? 'text-amber-300' : 'text-amber-700'}>
                          · {c.saturation_skips} saturation skips
                        </span>
                      )}
                      {(c.health_skips ?? 0) > 0 && (
                        <span className={isDark ? 'text-amber-300' : 'text-amber-700'}>
                          · {c.health_skips} health skips
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// One collapsible panel per request/response hop. Renders the payload as pretty-printed
// JSON in a monospace block; an empty payload shows a friendly "not captured" hint so
// older usage rows (pre-2026-06-06) don't look broken.
function TelemetryPanel({
  isDark,
  title,
  subtitle,
  payload,
  testId,
}: {
  isDark: boolean;
  title: string;
  subtitle: string;
  payload: unknown;
  testId: string;
}) {
  const [open, setOpen] = useState(false);
  const hasPayload =
    payload !== null && payload !== undefined && !(typeof payload === 'object' && Object.keys(payload).length === 0);
  const pretty = hasPayload ? JSON.stringify(payload, null, 2) : null;

  return (
    <div
      data-testid={testId}
      className={`rounded-xl border ${
        isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-slate-50/60'
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors ${
          isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-slate-100/60'
        }`}
      >
        <span className="flex items-center gap-2">
          {open ? (
            <ChevronDown
              className={`h-4 w-4 ${isDark ? 'text-white/50' : 'text-slate-500'}`}
              aria-hidden="true"
            />
          ) : (
            <ChevronRight
              className={`h-4 w-4 ${isDark ? 'text-white/50' : 'text-slate-500'}`}
              aria-hidden="true"
            />
          )}
          <span className={`text-sm font-medium ${isDark ? 'text-white/85' : 'text-slate-800'}`}>
            {title}
          </span>
          <span className={`text-[11px] ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
            {subtitle}
          </span>
        </span>
        {!hasPayload && (
          <span className={`text-[11px] italic ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
            not captured
          </span>
        )}
      </button>
      {open && hasPayload && (
        <pre
          className={`max-h-96 overflow-auto border-t px-4 py-3 font-mono text-[11px] leading-relaxed ${
            isDark
              ? 'border-white/[0.06] bg-black/40 text-white/80'
              : 'border-slate-200 bg-white text-slate-800'
          }`}
        >
          {pretty}
        </pre>
      )}
      {open && !hasPayload && (
        <p
          className={`border-t px-4 py-3 text-xs ${
            isDark
              ? 'border-white/[0.06] text-white/40'
              : 'border-slate-200 text-slate-500'
          }`}
        >
          No telemetry was stored for this request. This row may pre-date the per-hop
          capture feature, or the request failed before any upstream call.
        </p>
      )}
    </div>
  );
}

// ─── Building blocks ────────────────────────────────────────────────────────

function Section({
  isDark,
  title,
  icon,
  children,
}: {
  isDark: boolean;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h3
        className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest ${
          isDark ? 'text-white/40' : 'text-slate-400'
        }`}
      >
        {icon}
        {title}
      </h3>
      {children}
    </section>
  );
}

function FieldGrid({ isDark, children }: { isDark: boolean; children: React.ReactNode }) {
  return (
    <div
      className={`grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border p-4 sm:grid-cols-3 ${
        isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-slate-50'
      }`}
    >
      {children}
    </div>
  );
}

function Field({
  isDark,
  label,
  value,
  mono,
  tone,
}: {
  isDark: boolean;
  label: string;
  value: string;
  mono?: boolean;
  tone?: 'ok' | 'danger' | 'warn';
}) {
  const valueColor =
    tone === 'danger'
      ? isDark
        ? 'text-rose-300'
        : 'text-rose-600'
      : tone === 'warn'
        ? isDark
          ? 'text-amber-300'
          : 'text-amber-700'
        : tone === 'ok'
          ? isDark
            ? 'text-emerald-300'
            : 'text-emerald-700'
          : isDark
            ? 'text-white/80'
            : 'text-slate-700';

  return (
    <div className="min-w-0">
      <span
        className={`block text-[10px] font-medium uppercase tracking-widest ${
          isDark ? 'text-white/40' : 'text-slate-400'
        }`}
      >
        {label}
      </span>
      <span
        className={`mt-0.5 block truncate text-sm ${mono ? 'font-mono' : ''} ${valueColor}`}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}

function ErrorSection({
  isDark,
  attempts,
  rawErrorCode,
}: {
  isDark: boolean;
  attempts: ParsedAttempt[];
  rawErrorCode: string | null | undefined;
}) {
  return (
    <section
      className={`space-y-3 rounded-xl border p-4 ${
        isDark
          ? 'border-rose-500/30 bg-rose-500/[0.06]'
          : 'border-rose-200 bg-rose-50'
      }`}
    >
      <h3
        className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest ${
          isDark ? 'text-rose-300' : 'text-rose-700'
        }`}
      >
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        Error response
      </h3>

      {attempts.length === 0 ? (
        <p
          className={`font-mono text-xs ${
            isDark ? 'text-rose-200/80' : 'text-rose-700'
          }`}
        >
          {rawErrorCode || 'request failed — no per-attempt detail recorded'}
        </p>
      ) : (
        <>
          <p
            className={`text-xs ${
              isDark ? 'text-white/60' : 'text-slate-600'
            }`}
          >
            All {attempts.length} eligible provider{attempts.length === 1 ? '' : 's'} failed
            in order. Each row shows the provider that rejected and the summarized reason.
          </p>
          <ul className="space-y-2">
            {attempts.map((attempt, i) => (
              <AttemptRow
                key={`${attempt.provider}-${i}`}
                index={i}
                attempt={attempt}
                isDark={isDark}
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function AttemptRow({
  index,
  attempt,
  isDark,
}: {
  index: number;
  attempt: ParsedAttempt;
  isDark: boolean;
}) {
  return (
    <li
      className={`flex items-center gap-3 rounded-lg border p-3 ${
        isDark
          ? 'border-white/[0.06] bg-white/[0.02]'
          : 'border-slate-200 bg-white'
      }`}
    >
      <span
        className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full font-mono text-[11px] font-semibold ${
          isDark ? 'bg-white/[0.06] text-white/60' : 'bg-slate-100 text-slate-500'
        }`}
        aria-label={`Attempt ${index + 1}`}
      >
        {index + 1}
      </span>
      <div className="min-w-0 flex-1">
        <span
          className={`block text-sm font-medium ${
            isDark ? 'text-white/90' : 'text-slate-900'
          }`}
        >
          {providerLabelSafe(attempt.provider)}
        </span>
        <span
          className={`block truncate font-mono text-[11px] ${
            isDark ? 'text-rose-300' : 'text-rose-700'
          }`}
          title={attempt.reason}
        >
          {attempt.reason}
        </span>
      </div>
    </li>
  );
}

// Defensive wrapper for `providerLabel`: the parsed provider is a lowercase string that
// usually matches a `ProviderType` enum value, but if the error_code carries an unfamiliar
// token (e.g. from a future provider added at the backend without an SPA codegen pass) we
// want to render the raw string rather than crash.
function providerLabelSafe(raw: string): string {
  try {
    return providerLabel(raw.toUpperCase() as never);
  } catch {
    return raw;
  }
}
